import type {
  Contract,
  ContractCreatePayload,
  ContractDetail,
  ContractIndicacao,
  ContractListParams,
  ContractSignaturePayload,
  ContractTransitionPayload,
  ContractUpdatePayload,
  IndicacaoCreatePayload,
  IndicacaoUpdatePayload,
} from "@/types/contract";

import { api, type Paged, unwrap, unwrapWithMeta } from "./axiosConfig";

const base = (wsId: number) => `/api/v1/workspaces/${wsId}/contracts`;

export const contractService = {
  async list(workspaceId: number, params?: ContractListParams): Promise<Contract[]> {
    return unwrap(api.get(base(workspaceId), { params }));
  },

  /** FE-010: lista preservando `meta` (total real p/ KPIs e aviso de truncamento). */
  async listPaged(
    workspaceId: number,
    params?: ContractListParams,
  ): Promise<Paged<Contract[]>> {
    return unwrapWithMeta(api.get(base(workspaceId), { params }));
  },

  async get(workspaceId: number, id: number): Promise<ContractDetail> {
    return unwrap(api.get(`${base(workspaceId)}/${id}`));
  },

  async create(workspaceId: number, payload: ContractCreatePayload): Promise<ContractDetail> {
    return unwrap(api.post(base(workspaceId), payload));
  },

  async update(
    workspaceId: number,
    id: number,
    payload: ContractUpdatePayload,
  ): Promise<ContractDetail> {
    return unwrap(api.patch(`${base(workspaceId)}/${id}`, payload));
  },

  async remove(workspaceId: number, id: number): Promise<void> {
    await api.delete(`${base(workspaceId)}/${id}`);
  },

  async transition(
    workspaceId: number,
    id: number,
    to: ContractTransitionPayload["to"],
  ): Promise<Contract> {
    const payload: ContractTransitionPayload = { to };
    return unwrap(api.post(`${base(workspaceId)}/${id}/transitions`, payload));
  },

  async attachSignature(
    workspaceId: number,
    id: number,
    payload: ContractSignaturePayload,
  ): Promise<Contract> {
    return unwrap(api.post(`${base(workspaceId)}/${id}/signature`, payload));
  },

  // ─── Teses links ─────────────────────────────────────────────────────
  async addTese(workspaceId: number, id: number, teseId: number): Promise<ContractDetail> {
    return unwrap(api.post(`${base(workspaceId)}/${id}/teses`, { tese_id: teseId }));
  },

  async removeTese(workspaceId: number, id: number, linkId: number): Promise<void> {
    await api.delete(`${base(workspaceId)}/${id}/teses/${linkId}`);
  },

  // ─── Indicações ──────────────────────────────────────────────────────
  async addIndicacao(
    workspaceId: number,
    id: number,
    payload: IndicacaoCreatePayload,
  ): Promise<ContractDetail> {
    return unwrap(api.post(`${base(workspaceId)}/${id}/indicacoes`, payload));
  },

  async updateIndicacao(
    workspaceId: number,
    id: number,
    indId: number,
    payload: IndicacaoUpdatePayload,
  ): Promise<ContractIndicacao> {
    return unwrap(api.patch(`${base(workspaceId)}/${id}/indicacoes/${indId}`, payload));
  },

  async removeIndicacao(
    workspaceId: number,
    id: number,
    indId: number,
  ): Promise<void> {
    await api.delete(`${base(workspaceId)}/${id}/indicacoes/${indId}`);
  },
};
